import React, { useState } from 'react';
import {
  Wine,
  Rocket,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Share2,
  TrendingUp,
  Ghost,
  LogIn,
  LogOut,
  User,
  Plus,
  BookOpen,
  Globe
} from 'lucide-react';
import { generateDrunkIdea } from './services/ai';
import { useAuth } from './hooks/useAuth';
import { useHuntItems } from './hooks/useHuntItems';
import { useStories } from './hooks/useStories';
import { useDomains } from './hooks/useDomains';
import { signOut } from './services/auth';
import { createHuntItem, getHuntItemById, getStoryById, getHuntItemsByTimePeriod } from './services/firestore';
import AuthModal from './components/AuthModal';
import AddHuntModal from './components/AddHuntModal';
import AddStoryModal from './components/AddStoryModal';
import AddDomainModal from './components/AddDomainModal';
import ProductDetailModal from './components/ProductDetailModal';
import MakeOfferModal from './components/MakeOfferModal';
import DiscussModal from './components/DiscussModal';
import UserProfile from './components/UserProfile';
import FilterBar from './components/FilterBar';
import ShareButton from './components/ShareButton';
import { seedAllData } from './utils/seedData';
import logo from './assets/logo.png';

// ===== NAVBAR =====
const Navbar = ({ user, onSignIn, onSignOut, onProfileClick, onTabChange }) => (
  <nav className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-amber-900/30">
    <div className="container mx-auto px-4 h-16 flex items-center justify-between">
      <div className="flex items-center gap-3 cursor-pointer group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
        <img src={logo} alt="AlcoFounder Logo" className="w-10 h-10 rounded-lg transition-transform group-hover:scale-105" />
        <div className="text-xl font-bold tracking-wide text-white">
          Alco<span className="text-amber-500">Founder</span>
        </div>
      </div>

      <div className="hidden md:flex gap-8 text-sm font-medium">
        <button onClick={() => onTabChange('hunt')} className="text-slate-300 hover:text-amber-400 transition-colors cursor-pointer">Drunk Hunt</button>
        <button onClick={() => onTabChange('stories')} className="text-slate-400 hover:text-amber-400 transition-colors cursor-pointer">Stories</button>
        <button onClick={() => onTabChange('domains')} className="text-slate-400 hover:text-amber-400 transition-colors cursor-pointer">Domain Graveyard</button>
      </div>

      <div className="flex gap-3 items-center">
        {user ? (
          <>
            <button
              onClick={onProfileClick}
              className="hidden md:flex items-center gap-2 text-sm text-slate-300 hover:text-amber-400 transition cursor-pointer px-3 py-2 rounded-lg hover:bg-slate-800/50"
            >
              <User className="w-4 h-4" />
              <span>{user.displayName || user.email}</span>
            </button>
            <button
              onClick={onProfileClick}
              className="md:hidden text-slate-400 hover:text-amber-400 transition p-2"
            >
              <User className="w-5 h-5" />
            </button>
            <button
              onClick={onSignOut}
              className="text-slate-400 hover:text-white text-sm font-medium px-3 py-2 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </>
        ) : (
          <button
            onClick={onSignIn}
            className="text-slate-400 hover:text-white text-sm font-medium px-3 py-2 flex items-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            <span className="hidden md:inline">Login</span>
          </button>
        )}
      </div>
    </div>
  </nav>
);

// ===== ROTATING HEADER COMPONENT =====
const RotatingHeader = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  const headers = [
    {
      title: "Bad Ideas.",
      subtitle: "Great Ambitions.",
      tagline: "Startup generator for those tired of serious faces.",
      tagline2: "Choose your intoxication level or share your failure story."
    },
    {
      title: "Your 2 AM Ideas",
      subtitle: "Deserve a Home.",
      tagline: "Generate chaos, vote on brilliance, celebrate failures.",
      tagline2: "Where drunk ambitions meet the cold light of day."
    },
    {
      title: "Where Genius",
      subtitle: "Meets Regret.",
      tagline: "Adjust the madness level and watch the magic happen.",
      tagline2: "Your startup might suck, but at least it's documented."
    },
    {
      title: "Fail Fast.",
      subtitle: "Fail Drunk.",
      tagline: "From terrible ideas to terrible execution.",
      tagline2: "Vote on madness. Share your disasters. Buy failed domains."
    },
    {
      title: "Startup Ideas",
      subtitle: "With Liquid Courage.",
      tagline: "The only generator that gets worse ideas at higher settings.",
      tagline2: "Set your intoxication level and watch the chaos unfold."
    },
    {
      title: "Build Drunk.",
      subtitle: "Debug Sober.",
      tagline: "Where every idea is funded by regret.",
      tagline2: "Generate, vote, discuss—all fueled by questionable judgment."
    },
    {
      title: "Tomorrow's Regrets",
      subtitle: "Today's Ideas.",
      tagline: "Because every unicorn started as a bad idea.",
      tagline2: "Slide the chaos meter and see what happens."
    },
    {
      title: "Innovation",
      subtitle: "Meets Intoxication.",
      tagline: "The startup generator that shouldn't work, but does.",
      tagline2: "Share failures, vote on disasters, celebrate the absurd."
    },
    {
      title: "Liquid Courage.",
      subtitle: "Solid Failures.",
      tagline: "From buzzed brainstorming to documented disasters.",
      tagline2: "Your midnight epiphany probably isn't as good as you think."
    },
    {
      title: "Where Bad Ideas",
      subtitle: "Go to Party.",
      tagline: "The community for founders who peaked at 3‰.",
      tagline2: "Generate, vote, fail together."
    },
    {
      title: "Drunk Unicorns.",
      subtitle: "Sober Reality.",
      tagline: "Set the chaos slider and embrace the madness.",
      tagline2: "Every failed founder has a story. Share yours."
    },
    {
      title: "Zero Capital.",
      subtitle: "Infinite Chaos.",
      tagline: "The startup generator that encourages bad decisions.",
      tagline2: "Vote on ideas. Share disasters. Build something stupid."
    }
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % headers.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const current = headers[currentIndex];

  return (
    <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'}`}>
      <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight text-white tracking-tight">
        {current.title}<br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-500 to-red-500">
          {current.subtitle}
        </span>
      </h1>

      <p className="text-slate-400 text-lg md:text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
        {current.tagline} <br className="hidden md:block"/>
        {current.tagline2}
      </p>
    </div>
  );
};

// ===== GENERATOR WIDGET =====
const GeneratorWidget = ({ user, onPostSuccess, onOpenHuntModal }) => {
  const [level, setLevel] = useState(1.5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const getLabel = (val) => {
    if (val < 0.8) return { text: "Sober", color: "text-emerald-400" };
    if (val < 1.8) return { text: "Tipsy", color: "text-amber-400" };
    if (val < 2.5) return { text: "Wasted", color: "text-orange-500" };
    return { text: "Blackout", color: "text-red-500" };
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setResult(null);

    try {
      const idea = await generateDrunkIdea(parseFloat(level));
      setResult(idea);
    } catch (error) {
      console.error('Failed to generate idea:', error);
      alert('Failed to generate idea. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePostToHunt = () => {
    if (!user) {
      alert('Please login to post ideas!');
      return;
    }

    if (!result) return;

    // Open the AddHuntModal with prefilled data
    onOpenHuntModal?.(result.title, result.desc);
  };

  const labelInfo = getLabel(level);

  return (
    <div className="relative overflow-hidden w-full max-w-2xl mx-auto bg-slate-900/80 backdrop-blur-sm border border-slate-800 p-6 md:p-8 rounded-2xl shadow-2xl">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl"></div>

      <div className="flex justify-between items-end mb-6 relative z-10">
        <label className="text-sm font-bold text-slate-400 uppercase tracking-wider">
          Intoxication Level (Creativity)
        </label>
        <span className={`text-sm font-mono font-bold ${labelInfo.color}`}>
          {level}‰ — {labelInfo.text}
        </span>
      </div>

      <input
        type="range"
        min="0"
        max="3"
        step="0.1"
        value={level}
        onChange={(e) => setLevel(e.target.value)}
        className="w-full h-3 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-amber-500 mb-8 relative z-10"
      />

      {isGenerating ? (
        <div className="bg-slate-950/50 p-6 rounded-xl border border-slate-800 mb-6 flex flex-col items-center justify-center min-h-[140px] animate-pulse">
           <Zap className="w-8 h-8 text-amber-400 mb-2 animate-bounce" />
           <p className="text-slate-400">Pouring AI knowledge...</p>
        </div>
      ) : result ? (
        <div className="bg-slate-950/80 p-6 rounded-xl border border-amber-500/30 mb-6 min-h-[140px]">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-xl font-bold text-white bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
              {result.title}
            </h3>
            <span className="text-xs font-mono text-slate-500 border border-slate-800 px-2 py-1 rounded">
              {level}‰
            </span>
          </div>
          <p className="text-slate-300 text-sm leading-relaxed">{result.desc}</p>
          <div className="mt-4 flex gap-3">
             <button
               onClick={handlePostToHunt}
               className="text-xs flex items-center gap-1 text-slate-400 hover:text-emerald-400 transition"
             >
               <ThumbsUp className="w-3 h-3" /> Post to Hunt
             </button>
             <ShareButton
               title={result.title}
               description={result.desc}
               className="text-xs ml-auto"
             />
          </div>
        </div>
      ) : (
        <div className="bg-slate-950/30 border border-dashed border-slate-800 p-6 rounded-xl mb-6 min-h-[140px] flex items-center justify-center text-slate-500 text-sm">
           Click the button to generate a startup
        </div>
      )}

      <button
        onClick={handleGenerate}
        disabled={isGenerating}
        className="w-full relative z-10 bg-amber-500 hover:bg-amber-400 active:scale-[0.98] text-slate-900 font-bold py-4 rounded-xl transition flex justify-center items-center gap-2 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed group"
      >
        <span className="text-xl group-hover:rotate-12 transition-transform duration-300">🎲</span>
        {isGenerating ? "Brewing..." : "Generate Madness"}
      </button>
    </div>
  );
};

// ===== FEED ITEM =====
const FeedItem = ({ item, user, onVote, onClick }) => {
  const hasUpvoted = user && item.upvoters?.includes(user.uid);

  const handleLike = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (!user) {
      alert('Please login to vote!');
      return;
    }
    await onVote(item.id, user.uid, 'up');
  };

  return (
    <div onClick={onClick} className="group bg-slate-900/50 hover:bg-slate-900/90 border border-slate-800 hover:border-amber-500/30 backdrop-blur-sm p-6 rounded-xl flex gap-4 transition-all duration-300 cursor-pointer">
      <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center text-2xl md:text-3xl shadow-lg bg-gradient-to-br ${item.color} shrink-0`}>
        {item.icon}
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="text-lg md:text-xl font-bold text-white mb-1 break-words pr-2">
          {item.title}
          <span className="hidden md:inline-block text-[10px] border border-amber-500/20 text-amber-300 px-2 py-0.5 rounded uppercase tracking-wider ml-2">
            Startup
          </span>
        </h3>
        <p className="text-slate-400 text-sm leading-relaxed break-words pr-2">
          {item.desc}
        </p>
        <div className="flex flex-wrap gap-4 mt-3 text-xs font-bold text-slate-500 items-center">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 transition ${
              hasUpvoted ? 'text-emerald-400' : 'text-slate-500 hover:text-emerald-400'
            }`}
          >
            <ThumbsUp className={`w-3.5 h-3.5 ${hasUpvoted ? 'fill-current' : ''}`} />
            <span className="font-bold">{item.votes || 0}</span>
          </button>
          <span className="flex items-center gap-1 hover:text-amber-400 transition">
            <MessageSquare className="w-3 h-3" /> {item.comments || 0}
          </span>
          {item.tags?.map(tag => (
            <span key={tag} className="bg-slate-800 px-2 py-1 rounded text-slate-400">#{tag}</span>
          ))}
          <span className="text-amber-500/60 md:ml-0">@{item.author}</span>
          <div onClick={(e) => e.stopPropagation()} className="ml-auto">
            <ShareButton
              title={item.title}
              description={item.desc}
              itemId={item.id}
              itemType="hunt"
              className="text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== STORY ITEM =====
const StoryItem = ({ story, user, onSupport, onDiscuss }) => {
  const hasSupported = user && story.supporters?.includes(user.uid);

  const handleSupport = async () => {
    if (!user) {
      alert('Please login to support stories!');
      return;
    }
    await onSupport(story.id, user.uid);
  };

  const timeAgo = (date) => {
    if (!date) return 'recently';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  return (
    <div className="bg-slate-900/50 border-l-4 border-amber-600 p-6 md:p-8 rounded-r-xl hover:bg-slate-900 transition border border-r-0 border-y-slate-800 border-r-transparent cursor-pointer" onClick={() => onDiscuss(story)}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-slate-700 to-slate-600 flex items-center justify-center text-xs font-bold">
          {story.author?.[0] || '?'}
        </div>
        <div className="text-sm leading-tight">
          <span className="font-bold block text-white hover:text-amber-400 transition">
            {story.author}
          </span>
          <span className="text-slate-500 text-xs">{timeAgo(story.createdAt)}</span>
        </div>
      </div>
      <h3 className="text-xl md:text-2xl font-bold mb-3 text-slate-100 hover:text-amber-400 transition">
        {story.title}
      </h3>
      <p className="text-slate-300 leading-relaxed mb-6 text-sm md:text-base">
        {story.text}
      </p>
      <div className="flex gap-6 text-slate-400 text-sm font-bold border-t border-slate-800 pt-4" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={handleSupport}
          className={`flex items-center gap-2 transition ${hasSupported ? 'text-emerald-400' : 'hover:text-emerald-400'}`}
        >
          <Wine className="w-4 h-4" /> Support ({story.support || 0})
        </button>
        <button
          onClick={() => onDiscuss(story)}
          className="flex items-center gap-2 hover:text-white transition"
        >
          <MessageSquare className="w-4 h-4" /> Discuss ({story.comments || 0})
        </button>
        <ShareButton
          title={story.title}
          description={story.text}
          itemId={story.id}
          itemType="story"
          className="ml-auto"
        />
      </div>
    </div>
  );
};

// ===== DOMAIN LIST =====
const DomainList = ({ domains, onMakeOffer }) => (
  <div className="w-full">
    <div className="text-center mb-8">
       <h3 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
         <Ghost className="text-slate-400" /> Domain Graveyard
       </h3>
       <p className="text-slate-400">Where ambitions from Saturday night go to die.</p>
    </div>
    <div className="grid gap-4">
      {domains.map(domain => (
        <div key={domain.id} className="flex items-center justify-between bg-slate-900/50 p-4 rounded-lg border border-slate-800 hover:border-slate-700 transition">
          <div>
            <div className="font-mono text-lg text-amber-300 font-bold">{domain.name}</div>
            <div className="text-xs text-slate-500">Owner: {domain.owner || 'Available'}</div>
          </div>
          <div className="text-right">
             <div className="text-white font-bold">{domain.price}</div>
             <button
               onClick={() => onMakeOffer(domain)}
               className="text-xs text-amber-500 hover:text-white mt-1 underline decoration-dashed"
             >
               {domain.owner === 'Available' ? 'Buy Now' : 'Make Offer'}
             </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ===== GROUPED HUNT LIST (ProductHunt-style) =====
const GroupedHuntList = ({ user, onVote, onItemClick, refetchTrigger }) => {
  const [expandedSections, setExpandedSections] = useState({
    today: false,
    yesterday: false,
    week: false,
    month: false,
    allTime: false
  });

  const [grouped, setGrouped] = useState({
    today: [],
    yesterday: [],
    week: [],
    month: [],
    allTime: []
  });

  const [loading, setLoading] = useState(true);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Fetch items for each time period independently
  React.useEffect(() => {
    const fetchAllPeriods = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
        const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const veryOldDate = new Date(0); // Beginning of time

        // Fetch all periods in parallel
        const [todayItems, yesterdayItems, weekItems, monthItems, allTimeItems] = await Promise.all([
          getHuntItemsByTimePeriod(startOfToday, new Date(now.getTime() + 86400000), 20), // Today
          getHuntItemsByTimePeriod(startOfYesterday, startOfToday, 20), // Yesterday
          getHuntItemsByTimePeriod(startOfWeek, startOfYesterday, 20), // Last Week
          getHuntItemsByTimePeriod(startOfMonth, startOfWeek, 20), // Last Month
          getHuntItemsByTimePeriod(veryOldDate, startOfMonth, 20) // All Time
        ]);

        setGrouped({
          today: todayItems,
          yesterday: yesterdayItems,
          week: weekItems,
          month: monthItems,
          allTime: allTimeItems
        });
      } catch (error) {
        console.error('Error fetching grouped items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPeriods();
  }, [refetchTrigger]);

  const renderSection = (title, items, sectionKey, limit = 10) => {
    if (items.length === 0) return null;

    const isExpanded = expandedSections[sectionKey];
    const displayItems = isExpanded ? items : items.slice(0, limit);
    const hasMore = items.length > limit;

    return (
      <div className="mb-12">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <span className="text-amber-500">•</span> {title}
          <span className="text-sm text-slate-500 font-normal">({items.length})</span>
        </h3>
        <div className="space-y-4">
          {displayItems.map(item => (
            <FeedItem
              key={item.id}
              item={item}
              user={user}
              onVote={onVote}
              onClick={() => onItemClick(item)}
            />
          ))}
        </div>
        {hasMore && !isExpanded && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => toggleSection(sectionKey)}
              className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg transition"
            >
              See all of {title.toLowerCase()}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-400">
        Loading ideas...
      </div>
    );
  }

  const totalCount = grouped.today.length + grouped.yesterday.length + grouped.week.length + grouped.month.length + grouped.allTime.length;

  return (
    <div>
      {totalCount === 0 ? (
        <div className="text-center py-12 text-slate-400">
          No ideas yet. Generate one above or post your own!
        </div>
      ) : (
        <>
          {renderSection("Today's Top Ideas", grouped.today, 'today')}
          {renderSection("Yesterday's Top Ideas", grouped.yesterday, 'yesterday')}
          {renderSection("Last Week's Top Ideas", grouped.week, 'week')}
          {renderSection("Last Month's Top Ideas", grouped.month, 'month')}
          {renderSection("All-Time Top Ideas", grouped.allTime, 'allTime')}
        </>
      )}
    </div>
  );
};

// ===== MAIN APP =====
const App = () => {
  const [activeTab, setActiveTab] = useState('hunt');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showHuntModal, setShowHuntModal] = useState(false);
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showDiscuss, setShowDiscuss] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showMakeOffer, setShowMakeOffer] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [huntModalInitialTitle, setHuntModalInitialTitle] = useState('');
  const [huntModalInitialDesc, setHuntModalInitialDesc] = useState('');

  // Filter and sort state
  const [huntFilter, setHuntFilter] = useState('all');
  const [huntSort, setHuntSort] = useState('votes');
  const [storiesFilter, setStoriesFilter] = useState('all');
  const [storiesSort, setStoriesSort] = useState('support');
  const [domainsFilter, setDomainsFilter] = useState('all');
  const [domainsSort, setDomainsSort] = useState('price-high');

  const { user, loading: authLoading } = useAuth();
  const { items, loading: itemsLoading, loadingMore, vote, refetch: refetchHunt, loadMore, hasMore } = useHuntItems();
  const { stories, loading: storiesLoading, loadingMore: loadingMoreStories, support, refetch: refetchStories, loadMore: loadMoreStories, hasMore: hasMoreStories } = useStories();
  const { domains, loading: domainsLoading, loadingMore: loadingMoreDomains, refetch: refetchDomains, loadMore: loadMoreDomains, hasMore: hasMoreDomains } = useDomains();

  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Wrapper for vote that triggers grouped list refetch
  const handleVote = async (itemId, userId, voteType) => {
    await vote(itemId, userId, voteType);
    setRefetchTrigger(prev => prev + 1);
  };

  // Filtering and sorting logic
  const filterByTime = (items, filter) => {
    if (filter === 'all') return items;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return items.filter(item => {
      const itemDate = item.createdAt?.toDate ? item.createdAt.toDate() : item.createdAt;
      if (!itemDate) return true;

      switch (filter) {
        case 'today':
          return itemDate >= startOfToday;
        case 'week':
          return itemDate >= startOfWeek;
        case 'month':
          return itemDate >= startOfMonth;
        default:
          return true;
      }
    });
  };

  const sortItems = (items, sortBy) => {
    const sorted = [...items];

    switch (sortBy) {
      case 'votes':
        return sorted.sort((a, b) => (b.votes || 0) - (a.votes || 0));
      case 'support':
        return sorted.sort((a, b) => (b.support || 0) - (a.support || 0));
      case 'comments':
        return sorted.sort((a, b) => (b.comments || 0) - (a.comments || 0));
      case 'recent':
        return sorted.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt || new Date(0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt || new Date(0);
          return dateB - dateA;
        });
      case 'price-high':
        return sorted.sort((a, b) => {
          const priceA = parseFloat(a.price?.replace(/[^0-9.]/g, '') || '0');
          const priceB = parseFloat(b.price?.replace(/[^0-9.]/g, '') || '0');
          return priceB - priceA;
        });
      case 'price-low':
        return sorted.sort((a, b) => {
          const priceA = parseFloat(a.price?.replace(/[^0-9.]/g, '') || '0');
          const priceB = parseFloat(b.price?.replace(/[^0-9.]/g, '') || '0');
          return priceA - priceB;
        });
      default:
        return sorted;
    }
  };

  const filterDomains = (domains, filter) => {
    if (filter === 'all') return domains;
    if (filter === 'available') return domains.filter(d => d.owner === 'Available' || d.isAvailable);
    if (filter === 'owned') return domains.filter(d => d.owner !== 'Available' && !d.isAvailable);
    return domains;
  };

  // Apply filters and sorting
  const filteredHuntItems = sortItems(filterByTime(items, huntFilter), huntSort);
  const filteredStories = sortItems(filterByTime(stories, storiesFilter), storiesSort);
  const filteredDomains = sortItems(filterDomains(domains, domainsFilter), domainsSort);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const openAddModal = (type) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    switch (type) {
      case 'hunt':
        setShowHuntModal(true);
        break;
      case 'story':
        setShowStoryModal(true);
        break;
      case 'domain':
        setShowDomainModal(true);
        break;
    }
  };

  const handleProductClick = (item) => {
    setSelectedProduct(item);
    setShowProductDetail(true);
  };

  const handleDiscussClick = (story) => {
    setSelectedStory(story);
    setShowDiscuss(true);
  };

  const handleMakeOfferClick = (domain) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setSelectedDomain(domain);
    setShowMakeOffer(true);
  };

  // Handle URL parameters to open specific item modals
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const itemId = params.get('item');
    const itemType = params.get('type');

    if (itemId && itemType) {
      if (itemType === 'hunt') {
        // First check if item is already in loaded items
        const existingItem = items.find(i => i.id === itemId);
        if (existingItem) {
          setSelectedProduct(existingItem);
          setShowProductDetail(true);
          window.history.replaceState({}, '', window.location.pathname);
        } else if (!itemsLoading) {
          // If not in loaded items, fetch it directly from Firestore
          getHuntItemById(itemId).then(item => {
            if (item) {
              setSelectedProduct(item);
              setShowProductDetail(true);
              window.history.replaceState({}, '', window.location.pathname);
            }
          }).catch(err => {
            console.error('Error loading item from URL:', err);
          });
        }
      } else if (itemType === 'story') {
        // First check if story is already in loaded stories
        const existingStory = stories.find(s => s.id === itemId);
        if (existingStory) {
          setSelectedStory(existingStory);
          setShowDiscuss(true);
          window.history.replaceState({}, '', window.location.pathname);
        } else if (!storiesLoading) {
          // If not in loaded stories, fetch it directly from Firestore
          getStoryById(itemId).then(story => {
            if (story) {
              setSelectedStory(story);
              setShowDiscuss(true);
              window.history.replaceState({}, '', window.location.pathname);
            }
          }).catch(err => {
            console.error('Error loading story from URL:', err);
          });
        }
      }
    }
  }, [items, stories, itemsLoading, storiesLoading]);

  // Expose utility functions for dev/testing
  React.useEffect(() => {
    if (user) {
      window.seedAllData = () => seedAllData(user.uid, user.displayName || user.email);
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-amber-500 text-xl">Loading...</div>
      </div>
    );
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      document.getElementById('hunt')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-amber-500 selection:text-black pb-20">
      <Navbar
        user={user}
        onSignIn={() => setShowAuthModal(true)}
        onSignOut={handleSignOut}
        onProfileClick={() => setShowUserProfile(true)}
        onTabChange={handleTabChange}
      />

      {/* HERO SECTION */}
      <header className="relative pt-12 pb-16 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[500px] bg-amber-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="container mx-auto px-4 text-center max-w-4xl relative z-10">
          <div className="inline-block px-4 py-1.5 bg-amber-900/20 border border-amber-500/20 rounded-full text-amber-400 text-xs font-bold mb-6 uppercase tracking-widest shadow-[0_0_15px_rgba(245,158,11,0.1)]">
            Powered by Whiskey
          </div>

          <RotatingHeader />

          <GeneratorWidget
            user={user}
            onPostSuccess={refetchHunt}
            onOpenHuntModal={(title, desc) => {
              setHuntModalInitialTitle(title);
              setHuntModalInitialDesc(desc);
              setShowHuntModal(true);
            }}
          />
        </div>
      </header>

      {/* CONTENT TABS */}
      <main className="container mx-auto px-4 max-w-4xl" id="hunt">
        <div className="flex border-b border-slate-800 mb-8 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveTab('hunt')}
            className={`px-6 py-4 font-bold text-sm whitespace-nowrap transition-all border-b-2 ${
              activeTab === 'hunt'
                ? 'border-amber-500 text-white'
                : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-800'
            }`}
          >
            🔥 Drunk Hunt
          </button>
          <button
            onClick={() => setActiveTab('stories')}
            className={`px-6 py-4 font-bold text-sm whitespace-nowrap transition-all border-b-2 ${
              activeTab === 'stories'
                ? 'border-amber-500 text-white'
                : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-800'
            }`}
            id="stories"
          >
            📖 Bar Stories
          </button>
          <button
            onClick={() => setActiveTab('domains')}
            className={`px-6 py-4 font-bold text-sm whitespace-nowrap transition-all border-b-2 ${
              activeTab === 'domains'
                ? 'border-amber-500 text-white'
                : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-800'
            }`}
          >
            💀 Domain Graveyard
          </button>
        </div>

        {/* TAB CONTENT */}
        <div className="min-h-[400px]">
          {activeTab === 'hunt' && (
            <div>
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold text-white">Drunk Hunt</h2>
                <button
                  onClick={() => openAddModal('hunt')}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-4 py-2 rounded-lg transition flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Post Idea
                </button>
              </div>

              <GroupedHuntList
                user={user}
                onVote={handleVote}
                onItemClick={handleProductClick}
                refetchTrigger={refetchTrigger}
              />
            </div>
          )}

          {activeTab === 'stories' && (
            <div id="stories">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Bar Tales</h2>
                <button
                  onClick={() => openAddModal('story')}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-4 py-2 rounded-lg transition flex items-center gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  Share Story
                </button>
              </div>

              <FilterBar
                activeFilter={storiesFilter}
                onFilterChange={setStoriesFilter}
                activeSort={storiesSort}
                onSortChange={setStoriesSort}
                type="stories"
              />

              <div className="space-y-6">
                {storiesLoading ? (
                  <div className="text-center py-12 text-slate-400">Loading...</div>
                ) : filteredStories.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    No stories found. Try adjusting your filters or be the first to share!
                  </div>
                ) : (
                  filteredStories.map(story => (
                    <StoryItem
                      key={story.id}
                      story={story}
                      user={user}
                      onSupport={support}
                      onDiscuss={handleDiscussClick}
                    />
                  ))
                )}

                {/* Load More Button */}
                {hasMoreStories && filteredStories.length > 0 && (
                  <div className="flex justify-center pt-6">
                    <button
                      onClick={loadMoreStories}
                      disabled={loadingMoreStories}
                      className="px-6 py-3 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800/50 text-white font-bold rounded-lg transition flex items-center gap-2"
                    >
                      {loadingMoreStories ? 'Loading...' : 'Load More Stories'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'domains' && (
            <div id="domains">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Domain Graveyard</h2>
                <button
                  onClick={() => openAddModal('domain')}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-4 py-2 rounded-lg transition flex items-center gap-2"
                >
                  <Globe className="w-4 h-4" />
                  Add Domain
                </button>
              </div>

              <FilterBar
                activeFilter={domainsFilter}
                onFilterChange={setDomainsFilter}
                activeSort={domainsSort}
                onSortChange={setDomainsSort}
                type="domains"
              />

              {domainsLoading ? (
                <div className="text-center py-12 text-slate-400">Loading...</div>
              ) : filteredDomains.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  No domains found. Try adjusting your filters!
                </div>
              ) : (
                <>
                  <DomainList domains={filteredDomains} onMakeOffer={handleMakeOfferClick} />

                  {/* Load More Button */}
                  {hasMoreDomains && filteredDomains.length > 0 && (
                    <div className="flex justify-center pt-6">
                      <button
                        onClick={loadMoreDomains}
                        disabled={loadingMoreDomains}
                        className="px-6 py-3 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800/50 text-white font-bold rounded-lg transition flex items-center gap-2"
                      >
                        {loadingMoreDomains ? 'Loading...' : 'Load More Domains'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="mt-20 border-t border-slate-900 bg-slate-950 py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-between items-center gap-2 mb-4 opacity-50 hover:opacity-100 transition">
             <Wine className="w-5 h-5 text-amber-600" />
             <span className="font-bold text-white">AlcoFounder.com</span>
          </div>
          <p className="text-slate-600 text-sm mb-2">
            Made with love and poor judgment.
          </p>
          <p className="text-slate-700 text-xs">
            Not financial advice. Please do not deploy on Friday evenings.
          </p>
        </div>
      </footer>

      {/* MODALS */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
        }}
      />

      <AddHuntModal
        isOpen={showHuntModal}
        onClose={() => {
          setShowHuntModal(false);
          setHuntModalInitialTitle('');
          setHuntModalInitialDesc('');
        }}
        user={user}
        onSuccess={() => {
          setShowHuntModal(false);
          setHuntModalInitialTitle('');
          setHuntModalInitialDesc('');
          refetchHunt();
          setRefetchTrigger(prev => prev + 1); // Trigger refetch for grouped list
        }}
        initialTitle={huntModalInitialTitle}
        initialDesc={huntModalInitialDesc}
      />

      <AddStoryModal
        isOpen={showStoryModal}
        onClose={() => setShowStoryModal(false)}
        user={user}
        onSuccess={() => {
          setShowStoryModal(false);
          refetchStories();
        }}
      />

      <AddDomainModal
        isOpen={showDomainModal}
        onClose={() => setShowDomainModal(false)}
        user={user}
        onSuccess={() => {
          setShowDomainModal(false);
          refetchDomains();
        }}
      />

      <ProductDetailModal
        isOpen={showProductDetail}
        onClose={() => setShowProductDetail(false)}
        item={selectedProduct}
        user={user}
        onVote={vote}
      />

      <DiscussModal
        isOpen={showDiscuss}
        onClose={() => setShowDiscuss(false)}
        story={selectedStory}
        user={user}
      />

      <MakeOfferModal
        isOpen={showMakeOffer}
        onClose={() => setShowMakeOffer(false)}
        domain={selectedDomain}
      />

      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        userId={user?.uid}
        userName={user?.displayName}
        userEmail={user?.email}
      />
    </div>
  );
};

export default App;
